import type { ComponentProps, FC, ReactElement } from "react"
import { Fragment } from "react"

import eq from "lodash-es/eq"

import {
    ESpecialChar,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    SpecialChars,
    Typography
} from "@/3514/components"
// dont short this import, adin chapter page will fail bcc of broken vite load
import { ETypographyAlignment } from "@/3514/components/shared/typography"
import { EFeedbackModalType, EFeedbackModalVariant } from "@/3514/modals"
import { withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { coachingChatUiConfig as uiConfig } from "../config"
import { useCoachingChatContext as useChatContext } from "../context"
import { coachingChatStyles as chatStyles } from "../styles"
import { type TCoachingChatConversationError as TConversationError } from "../types"

type TVariant = "seeds" | "conversation"

type TProps = {
    moderationId: Pick<TConversationError, "payload">["payload"]["moderationId"]
    modalVariant:
        | EFeedbackModalVariant.SeedViolation
        | EFeedbackModalVariant.SeedApiError
        | EFeedbackModalVariant.ChatViolation
    variant: TVariant
}

const {
    conversation: { violationFeedbackOpener: classes }
} = chatStyles

const { reportFeedbackOpenerTextsMap: openerTextsMap } = uiConfig

const typographyPropsMap: {
    [V in TVariant]: Partial<ComponentProps<typeof Typography>>
} = {
    seeds: {
        alignment: ETypographyAlignment.Center,
        size: ETypographySize.Tiny,
        color: ETypographyColor.White
    },
    conversation: {
        alignment: ETypographyAlignment.Left,
        size: ETypographySize.ExtraSmall,
        color: ETypographyColor.Black,
        isItalic: true
    }
}

const openerLinkPropsMap: { [V in TVariant]: Partial<ComponentProps<typeof Typography>> } = {
    seeds: {
        size: ETypographySize.Tiny,
        color: ETypographyColor.White,
        weight: ETypographyWeight.Bold
    },
    conversation: {
        size: ETypographySize.Tiny,
        color: ETypographyColor.Accent,
        isItalic: true
    }
}

const Opener: FC<TProps> = ({
    moderationId,
    variant = "seeds",
    modalVariant = EFeedbackModalVariant.SeedViolation
}: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        feedback: { handleModalOpen }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const handleAction: ReturnType<typeof withPressHandler> = withPressHandler((): void =>
        handleModalOpen({
            variant: modalVariant,
            type: EFeedbackModalType.Report,
            payload: { moderationId }
        })
    )

    return (
        <Typography
            {...typographyPropsMap[variant]}
            tag={ETypographyTag.Div}
            text={
                <Fragment>
                    {t(openerTextsMap[variant].firstPart)}

                    {SpecialChars[ESpecialChar.EmptySpace]}

                    <div
                        role="link"
                        tabIndex={0}
                        onClick={handleAction}
                        onKeyDown={handleAction}
                        className={classes.wrapper()}
                    >
                        <Typography
                            className={classes.action({
                                isAccent: eq(variant, "conversation"),
                                isWhite: eq(variant, "seeds")
                            })}
                            tag={ETypographyTag.Span}
                            text={t(openerTextsMap[variant].action)}
                            {...openerLinkPropsMap[variant]}
                        />

                        {SpecialChars[ESpecialChar.Dot]}

                        {SpecialChars[ESpecialChar.EmptySpace]}

                        {t(openerTextsMap[variant].lastPart)}
                    </div>
                </Fragment>
            }
        />
    )
}

Opener.displayName = "CoachingChatReportFeedbackOpener"

export { Opener as CoachingChatReportFeedbackOpener }
